// src/sim/match.ts — Maç akışı (DESIGN §13, §8): geri sayım (3 s, inişle örtüşür), canlar, düşme atfı (180 t), yeniden doğuş
// (90 t, en uzak spawn, Spawn_Air kontrol 54 t, koruma 90 t), eleme, kazanan, globalHitstop.
// Adım 1 (stepMatchTimers) ve adım 9 (stepMatch); kill plane → fell(). three'siz, tahsissiz.
//
// AĞIR ÇEKİM BURADA DEĞİL (NETWORK.md karar 4): sim her zaman 60 Hz akar, sunucu hiç yavaşlamaz. §10 mini ağır çekim
// istemcinin YEREL gösterisidir; tetiği `Ev.Downed` olayı taşır (hits.ts / grab.ts basar), pencereyi ve zaman eğrisini
// `src/view/slowMo.ts` tutar. Maç sonu yavaşlatması da `Ev.MatchEnd`ten istemcide kurulur.
// Eskiden `slowMoUntil` hash'lenen bir sim alanıydı ve tetik kuralı "vuran/yiyen !isBot" idi — ağda uzak insan da
// isBot:false olduğu için iki yabancının dövüşü herkesin ekranını yavaşlatırdı.
import { Ev, FState } from '../core/types.ts';
import type { Fighter, MatchState, SimWorld } from '../core/types.ts';
import { NO_ID, NO_TICK, TICK_HZ } from '../config/constants.ts';
import { clamp01, lerp, smoothstep } from '../core/math.ts';
import { T } from '../config/tuning.ts';
import { placeFighter, setState } from './fighter.ts';

/** 2B uzunluk: Math.hypot V8'de FixedDoubleArray tahsis eder (sıcak yol yasağı) → sqrt(a²+b²). */
const hyp = (a: number, b: number): number => Math.sqrt(a * a + b * b);

const FAR_FUTURE = 0x3fffffff;

export function createMatchState(): MatchState {
  return { phase: 'countdown', phaseUntil: 0, countdown: 0, winner: NO_ID, globalHitstop: 0 };
}

/** Spawn noktası yaw'ı: arena merkezine bakar. */
function yawToCenter(world: SimWorld, x: number, z: number): number {
  const dx = world.arena.center.x - x, dz = world.arena.center.z - z;
  return dx === 0 && dz === 0 ? 0 : Math.atan2(dx, dz);
}

/** Maç başı: herkes spawn noktasına, Spawn_Air (Spawning 60 t), girdi geri sayım boyunca (180 t) kapalı; Ev.Countdown 3 adım 1'de. */
export function beginCountdown(world: SimWorld): void {
  const tick = world.tick;
  const m = world.match;
  const total = T.RESPAWN.countdownStep * 3;
  m.phase = 'countdown'; m.phaseUntil = tick + total; m.countdown = 3; m.winner = NO_ID;
  const spawns = world.arena.spawns;
  for (let i = 0; i < world.fighters.length; i++) {
    const f = world.fighters[i]!;
    const sp = spawns[i % spawns.length]!;
    placeFighter(f, sp, yawToCenter(world, sp.x, sp.z), tick);
    f.lives = T.RESPAWN.lives; f.falls = 0; f.kos = 0; f.fellAt = NO_TICK; f.respawnAt = NO_TICK; f.spawnProtUntil = NO_TICK;
    setState(f, FState.Spawning, tick, tick + T.RESPAWN.spawnAirTicks);
    f.controlLockUntil = tick + total;
  }
}

/** Testler / duman: geri sayımı atla, hemen dövüş (herkes Idle, girdi açık). */
export function startFight(world: SimWorld): void {
  const tick = world.tick;
  world.match.phase = 'fight'; world.match.phaseUntil = tick; world.match.countdown = 0;
  for (let i = 0; i < world.fighters.length; i++) {
    const f = world.fighters[i]!;
    if (f.state === FState.Spawning) setState(f, FState.Idle, tick);
    f.controlLockUntil = NO_TICK;
  }
}

/** Adım 1: globalHitstop (true → adım 2–8 atlanır), geri sayım olayları ve DÖVÜŞ geçişi. */
export function stepMatchTimers(world: SimWorld): boolean {
  const m = world.match;
  const tick = world.tick;
  if (m.phase === 'countdown') {
    const step = T.RESPAWN.countdownStep;
    const start = m.phaseUntil - step * 3;
    const el = tick - start;
    if (el >= 0 && el < step * 3 && el % step === 0) {
      m.countdown = 3 - el / step;
      const e = world.events.push(Ev.Countdown, tick, NO_ID); e.v0 = m.countdown;
    }
    if (tick >= m.phaseUntil) {
      m.phase = 'fight'; m.countdown = 0;
      world.events.push(Ev.MatchStart, tick, NO_ID);
      for (let i = 0; i < world.fighters.length; i++) { const f = world.fighters[i]!; if (f.controlLockUntil > tick) f.controlLockUntil = tick; }
    }
  }
  if (m.globalHitstop > 0) { m.globalHitstop--; return true; }
  return false;
}

/**
 * Düşenin tutma bağlarını çözer (DESIGN §3/§6 "taşıyan kenardan yürüyüp ikisi birden düşebilir"; düzeltici tur 2): tutan/taşıyan
 * düşüyorsa Held/Carried kurban onun soketinde, platform dışındadır → aynı tick ring-out (atıf: lastHitBy = tutan, tryGrab yazmıştı).
 * Kavranmamış G3 kurbanı (hâlâ Lying, kendi yerinde) yalnız çözülür. Kurban düşüyorsa tutan serbest kalır (Hold/Lifting/Carrying/
 * PickingUp → Idle; Throwing bırakma sonrası toparlanmayı grab.ts kendisi yürütür; Carry omuz çarpması endMove'da Idle'a döner).
 * Yalnız düşenin alanlarını temizlemek kurbanı yetim Carried (yerçekimsiz, adımlanmayan, killY üstünde) bırakıyordu → maç bitemiyordu.
 */
function releaseGrabBonds(world: SimWorld, f: Fighter): void {
  const tick = world.tick;
  if (f.grabTarget !== NO_ID) {
    const v = world.fighters[f.grabTarget];
    f.grabTarget = NO_ID; f.holdSince = NO_TICK; f.carryUntil = NO_TICK;
    if (v && v.grabbedBy === f.id) {
      v.grabbedBy = NO_ID; v.holdSince = NO_TICK;
      if (v.state === FState.Held || v.state === FState.Carried) {
        v.grounded = false;
        setState(v, FState.Air, tick);
        fell(world, v);   // v.grabTarget NO_ID → özyineleme burada biter
      }
    }
  }
  if (f.grabbedBy !== NO_ID) {
    const h = world.fighters[f.grabbedBy];
    f.grabbedBy = NO_ID; f.holdSince = NO_TICK;
    if (h && h.grabTarget === f.id) {
      h.grabTarget = NO_ID; h.holdSince = NO_TICK; h.carryUntil = NO_TICK;
      const s = h.state;
      if (s === FState.Hold || s === FState.Lifting || s === FState.Carrying || s === FState.PickingUp) {
        h.moveIdx = -1; h.moveTick = 0; h.phase = 0; h.stageIdx = 0; h.stageStartTick = NO_TICK;
        setState(h, FState.Idle, tick);
      }
    }
  }
}

/**
 * Ring-out (kill plane; collision/world çağırır): atıf (lastHitBy son 180 t), can −1, Ev.Fell (target = puanı alan / −1, v0 kalan can),
 * tüm sahne hitstop 7 t, silah kurbanla yok olur, Falling → 90 t sonra yeniden doğuş; 0 can → Eliminated; son can → slow-mo isteği.
 */
export function fell(world: SimWorld, f: Fighter): void {
  const tick = world.tick;
  if (f.state === FState.Falling || f.state === FState.Eliminated) return;
  let scorer = NO_ID;
  if (f.lastHitBy !== NO_ID && f.lastHitBy !== f.id && tick - f.lastHitTick <= T.RESPAWN.attributionTicks) {
    scorer = f.lastHitBy;
    const s = world.fighters[scorer]; if (s) s.kos++;
  }
  f.falls++; f.lives--; f.fellAt = tick;
  // silah kurbanla yok olur (DESIGN §8): tutulan eşyalar ölür
  const items = world.items;
  for (let i = 0; i < items.length; i++) { const it = items[i]!; if (it.state === 'held' && it.holder === f.id) { it.state = 'dead'; it.holder = NO_ID; } }
  f.weapon = 'unarmed'; f.weaponItem = NO_ID; f.weaponItemL = NO_ID; f.shieldItem = NO_ID; f.hasShield = false;
  f.vel.x = 0; f.vel.y = 0; f.vel.z = 0; f.rmDx = 0; f.rmDz = 0;
  f.hitstop = 0; f.blocking = false; f.moveIdx = -1; f.phase = 0;
  releaseGrabBonds(world, f);
  const e = world.events.push(Ev.Fell, tick, f.id, scorer); e.v0 = f.lives;
  e.x = f.pos.x; e.y = f.pos.y; e.z = f.pos.z;
  world.match.globalHitstop = Math.max(world.match.globalHitstop, T.HITSTOP.ringout);
  if (f.lives <= 0) {
    f.lives = 0;
    setState(f, FState.Eliminated, tick);
    f.respawnAt = NO_TICK; f.controlLockUntil = FAR_FUTURE;
    world.events.push(Ev.Eliminated, tick, f.id);
  } else {
    setState(f, FState.Falling, tick);
    f.respawnAt = tick + T.RESPAWN.delayTicks; f.controlLockUntil = FAR_FUTURE;
  }
}

/** Tüm rakiplere en uzak spawn: rakiplerin en yakınına olan mesafeyi en büyükleyen nokta (beraberlikte ilk). */
function farthestSpawn(world: SimWorld, self: Fighter): number {
  const spawns = world.arena.spawns;
  let best = 0, bestScore = -Infinity;
  for (let s = 0; s < spawns.length; s++) {
    const p = spawns[s]!;
    let minD = Infinity;
    for (let i = 0; i < world.fighters.length; i++) {
      const o = world.fighters[i]!;
      if (o === self || o.state === FState.Eliminated || o.state === FState.Falling) continue;
      const d = hyp(o.pos.x - p.x, o.pos.z - p.z);
      if (d < minD) minD = d;
    }
    if (minD > bestScore) { bestScore = minD; best = s; }
  }
  return best;
}

/** Yeniden doğuş: en uzak spawn, Spawn_Air (Respawning; kontrol 54 t'de → Idle), koruma 90 t, dmg/stun 0, guard tam, silah yok. */
export function respawn(world: SimWorld, f: Fighter): void {
  const tick = world.tick;
  const sp = world.arena.spawns[farthestSpawn(world, f)]!;
  placeFighter(f, sp, yawToCenter(world, sp.x, sp.z), tick);
  f.respawnAt = NO_TICK; f.fellAt = NO_TICK;
  setState(f, FState.Respawning, tick, tick + T.RESPAWN.controlAt);
  f.controlLockUntil = tick + T.RESPAWN.controlAt;
  f.spawnProtUntil = tick + T.RESPAWN.protectionTicks;
  const e = world.events.push(Ev.Respawn, tick, f.id); e.x = sp.x; e.y = sp.y; e.z = sp.z;
}

/** Adım 9: yeniden doğuşlar, eleme sonrası kazanan. (Maç sonu yavaşlatması SUNUMDA: Ev.MatchEnd → view/slowMo.ts.) */
export function stepMatch(world: SimWorld): void {
  const tick = world.tick;
  const m = world.match;
  const fs = world.fighters;
  for (let i = 0; i < fs.length; i++) {
    const f = fs[i]!;
    if (f.state === FState.Falling && f.respawnAt !== NO_TICK && tick >= f.respawnAt) respawn(world, f);
  }
  if (m.phase === 'fight' && fs.length >= 2) {
    let alive = 0, last = NO_ID;
    for (let i = 0; i < fs.length; i++) { const f = fs[i]!; if (f.state !== FState.Eliminated) { alive++; last = f.id; } }
    if (alive <= 1) {
      m.phase = 'over'; m.phaseUntil = tick; m.winner = last;
      for (let i = 0; i < fs.length; i++) fs[i]!.controlLockUntil = FAR_FUTURE;
      const e = world.events.push(Ev.MatchEnd, tick, last); e.v0 = last;
    }
  }
}
